<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps({
    images: { type: Array, default: () => [] },
    index: { type: Number, default: null },
    /**
     * Optional, parallel to `images`. Ten surfaces already open this
     * component with a bare list of URLs, so captions are an extra
     * rather than a change of shape — a caption-less caller passes
     * nothing and renders exactly as before.
     *
     * An entry may be empty: a gallery where three of eight pictures
     * are described should show three descriptions, not eight bars
     * with five of them blank.
     */
    captions: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

// Refs to the image + overlay so we can attach non-passive touch
// listeners programmatically (Vue's @touchmove.prevent binds via
// its own path that some WKWebView / Chrome flag combinations
// still treat as passive; the native pinch then wins the gesture
// and our onImgTouchMove never gets to preventDefault). Also lets
// us bind `gesturestart/change/end` (iOS-specific webkit events
// that fire even when touch-action:none is set) and defeat the
// WKWebView's built-in pinch-to-zoom that way.
const imgEl = ref(null);
const overlayEl = ref(null);

const current = ref(props.index ?? 0);

watch(() => props.index, (v) => { if (v !== null) current.value = v; });

const isOpen = computed(() => props.index !== null && props.images.length > 0);

const prev = () => { current.value = (current.value - 1 + props.images.length) % props.images.length; };
const next = () => { current.value = (current.value + 1) % props.images.length; };

// ── Pinch-zoom / pan / double-tap ────────────────────────────────
// Simple 2-D transform on the image element: scale ∈ [1, 5], tx/ty
// in CSS pixels. All gestures are implemented ourselves rather than
// relying on the browser's native pinch-zoom — the lightbox lives
// inside a `overflow:hidden` overlay so native page zoom doesn't
// apply, and we need drag / double-tap consistency across iOS
// WebView + desktop + Android anyway.
const scale = ref(1);
const tx = ref(0);
const ty = ref(0);
const isPanning = ref(false);
const isPinching = ref(false);
// Gesture start snapshots — reset per touchstart. Keys mirror the
// two independent gestures so a two-finger pinch that lifts one
// finger degrades smoothly into a one-finger pan without a jump.
const _pinchStart = { dist: 0, scale: 1, tx: 0, ty: 0, cx: 0, cy: 0 };
const _panStart = { x: 0, y: 0, tx: 0, ty: 0 };
// iOS WKWebView / Safari deliver a two-finger pinch as WebKit
// GestureEvents (cumulative e.scale) and do NOT reliably deliver the
// two-finger `touchmove` (touches.length===2) our touch path keys off.
// Where GestureEvent exists we drive zoom from it directly; elsewhere
// (Android / Chrome) the touch path below handles pinch.
const supportsGesture = typeof window !== 'undefined' && 'GestureEvent' in window;
const _gestureStart = { scale: 1, tx: 0, ty: 0, cx: 0, cy: 0 };
let _lastTapMs = 0;
let _lastTapX = 0;
let _lastTapY = 0;
// Set whenever a gesture actually moved anything; consumed by the
// backdrop click handler so a pinch that ends inside the overlay
// doesn't get read as "click background → close".
let _gestured = false;

function resetZoom() {
    scale.value = 1;
    tx.value = 0;
    ty.value = 0;
}

// Whenever the image slot changes (prev / next / new open),
// snap back to fit — a zoomed-in view of the previous photo has
// no meaning against the next one.
watch(current, resetZoom);
watch(() => props.index, (v) => { if (v === null) resetZoom(); });

function _dist(t1, t2) {
    return Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
}

// Toggle 1× ↔ 2.5× centred on (clientX, clientY). Extracted so
// both touch double-tap and desktop dblclick can call it.
function toggleZoomAt(clientX, clientY, containerRect) {
    if (scale.value > 1.01) {
        resetZoom();
        return;
    }
    const cx = containerRect.left + containerRect.width / 2;
    const cy = containerRect.top + containerRect.height / 2;
    const target = 2.5;
    scale.value = target;
    tx.value = (cx - clientX) * (target - 1);
    ty.value = (cy - clientY) * (target - 1);
    _gestured = true;
}

function onImgDblClick(evt) {
    evt.preventDefault();
    toggleZoomAt(evt.clientX, evt.clientY, evt.currentTarget.getBoundingClientRect());
}

function onImgTouchStart(evt) {
    if (!supportsGesture && evt.touches.length === 2) {
        // Two-finger pinch — snapshot the initial distance +
        // midpoint so the move handler can compute a smooth
        // scale/translate delta.
        isPinching.value = true;
        isPanning.value = false;
        _pinchStart.dist = _dist(evt.touches[0], evt.touches[1]);
        _pinchStart.scale = scale.value;
        _pinchStart.tx = tx.value;
        _pinchStart.ty = ty.value;
        _pinchStart.cx = (evt.touches[0].clientX + evt.touches[1].clientX) / 2;
        _pinchStart.cy = (evt.touches[0].clientY + evt.touches[1].clientY) / 2;
        _gestured = true;
        evt.preventDefault();
        return;
    }
    if (evt.touches.length === 1) {
        // Double-tap detection: two taps within 320ms + ~40px of
        // each other toggle zoom in / out. Zoomed-out → 2.5×
        // centred on the tap point; zoomed-in → reset.
        const now = Date.now();
        const t = evt.touches[0];
        const isDoubleTap = (now - _lastTapMs) < 320
            && Math.hypot(t.clientX - _lastTapX, t.clientY - _lastTapY) < 40;
        if (isDoubleTap) {
            _lastTapMs = 0;
            toggleZoomAt(t.clientX, t.clientY, evt.currentTarget.getBoundingClientRect());
            evt.preventDefault();
            return;
        }
        _lastTapMs = now;
        _lastTapX = t.clientX;
        _lastTapY = t.clientY;
        // Single-touch pan is only meaningful when the image is
        // zoomed — otherwise let the browser handle the touch (so
        // tapping the image doesn't feel dead).
        if (scale.value > 1.01) {
            isPanning.value = true;
            _panStart.x = t.clientX;
            _panStart.y = t.clientY;
            _panStart.tx = tx.value;
            _panStart.ty = ty.value;
            evt.preventDefault();
        }
    }
}

function onImgTouchMove(evt) {
    if (!supportsGesture && isPinching.value && evt.touches.length === 2) {
        const newDist = _dist(evt.touches[0], evt.touches[1]);
        const ratio = newDist / (_pinchStart.dist || 1);
        const nextScale = Math.max(1, Math.min(5, _pinchStart.scale * ratio));
        scale.value = nextScale;
        // Follow the pinch midpoint so the two fingers stay anchored
        // to the image points they started on — the classic
        // "photos.app" feel.
        const newCx = (evt.touches[0].clientX + evt.touches[1].clientX) / 2;
        const newCy = (evt.touches[0].clientY + evt.touches[1].clientY) / 2;
        tx.value = _pinchStart.tx + (newCx - _pinchStart.cx);
        ty.value = _pinchStart.ty + (newCy - _pinchStart.cy);
        _gestured = true;
        evt.preventDefault();
        return;
    }
    if (isPanning.value && evt.touches.length === 1) {
        tx.value = _panStart.tx + (evt.touches[0].clientX - _panStart.x);
        ty.value = _panStart.ty + (evt.touches[0].clientY - _panStart.y);
        _gestured = true;
        evt.preventDefault();
    }
}

function onImgTouchEnd(evt) {
    // A two-finger pinch that drops to one finger becomes a pan.
    if (evt.touches.length === 1 && isPinching.value) {
        isPinching.value = false;
        if (scale.value > 1.01) {
            isPanning.value = true;
            _panStart.x = evt.touches[0].clientX;
            _panStart.y = evt.touches[0].clientY;
            _panStart.tx = tx.value;
            _panStart.ty = ty.value;
        }
        return;
    }
    if (evt.touches.length === 0) {
        isPinching.value = false;
        isPanning.value = false;
        // Snap fully-out to centred so the next tap doesn't feel
        // stuck on a stale ~1.005 scale.
        if (scale.value <= 1.01) resetZoom();
    }
}

// iOS / WebKit pinch path. GestureEvents carry a cumulative `scale`
// (1.0 at gesturestart) plus the gesture centroid in clientX/clientY.
// We anchor scale off the value at gesturestart and translate to
// follow the centroid, mirroring the touch pinch's "photos.app" feel.
// preventDefault on each keeps the WebView's own page zoom out of it.
function onGestureStart(evt) {
    if (!isOpen.value) return;
    evt.preventDefault();
    isPinching.value = true;
    isPanning.value = false;
    _gestureStart.scale = scale.value;
    _gestureStart.tx = tx.value;
    _gestureStart.ty = ty.value;
    _gestureStart.cx = evt.clientX;
    _gestureStart.cy = evt.clientY;
    _gestured = true;
}

function onGestureChange(evt) {
    if (!isOpen.value) return;
    evt.preventDefault();
    // Recover a missed gesturestart (some WKWebView builds fire
    // gesturechange first) so we never no-op the whole pinch.
    if (!isPinching.value) onGestureStart(evt);
    scale.value = Math.max(1, Math.min(5, _gestureStart.scale * evt.scale));
    tx.value = _gestureStart.tx + (evt.clientX - _gestureStart.cx);
    ty.value = _gestureStart.ty + (evt.clientY - _gestureStart.cy);
    _gestured = true;
}

function onGestureEnd(evt) {
    if (!isOpen.value) return;
    evt.preventDefault();
    isPinching.value = false;
    if (scale.value <= 1.01) resetZoom();
    _gestured = true;
}

// Desktop parity — Ctrl/Cmd + wheel zooms towards the cursor,
// mirroring how browsers handle native page zoom. Without the
// modifier, the wheel does nothing (so a trackpad scroll doesn't
// accidentally zoom the image while the user meant to scroll the
// page behind the overlay).
function onImgWheel(evt) {
    if (!evt.ctrlKey && !evt.metaKey) return;
    evt.preventDefault();
    const rect = evt.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const before = scale.value;
    const factor = Math.exp(-evt.deltaY * 0.005);
    const nextScale = Math.max(1, Math.min(5, before * factor));
    if (nextScale === before) return;
    // Adjust translate so the point under the cursor stays under
    // the cursor across the scale change.
    const px = evt.clientX - cx - tx.value;
    const py = evt.clientY - cy - ty.value;
    const k = nextScale / before;
    tx.value = tx.value + px - px * k;
    ty.value = ty.value + py - py * k;
    scale.value = nextScale;
    if (nextScale <= 1.01) resetZoom();
    _gestured = true;
}

// Backdrop click closes UNLESS a gesture just ended inside the
// overlay — otherwise a pinch that releases with both fingers on
// the dark surround would immediately dismiss the lightbox.
function onBackdropClick() {
    if (_gestured) { _gestured = false; return; }
    emit('close');
}

const imgTransformStyle = computed(() => ({
    transform: `translate(${tx.value}px, ${ty.value}px) scale(${scale.value})`,
    transformOrigin: 'center center',
    // No transition during active gestures — the transform should
    // track the fingers 1:1. A short ease when the gesture ends
    // (via .end handlers) smooths any final snap-to-1×.
    transition: isPanning.value || isPinching.value ? 'none' : 'transform 120ms ease-out',
    touchAction: 'none',
    willChange: 'transform',
    cursor: scale.value > 1.01 ? 'grab' : 'zoom-in',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    WebkitUserDrag: 'none',
    WebkitTouchCallout: 'none',
}));

// Save the current image to the device's Photos library on
// Capacitor (opens the share sheet with Save Image as one of
// the options), or triggers a plain browser download in a web
// build. Best-effort — silently no-ops when neither path is
// available so a tap never throws.
const downloading = ref(false);
const doDownload = async () => {
    if (downloading.value) return;
    const url = props.images[current.value];
    if (!url) return;
    downloading.value = true;
    try {
        const cap = typeof window !== 'undefined' ? window.Capacitor : null;
        const isNative = cap?.isNativePlatform?.();
        if (isNative) {
            // Capacitor path — fetch image → write to CACHE
            // dir → resolve to a file:// URI → hand the URI to
            // @capacitor/share.share({ files }). The iOS share
            // sheet opens with Save Image at the top (single tap
            // → Photos library, using the NSPhotoLibraryAddUsage
            // permission from Info.plist), and Save to Files as
            // the next option. CACHE (not DOCUMENTS) so we don't
            // pollute the Files app with every download.
            // `Directory` is an enum EXPORTED by @capacitor/
            // filesystem, not a plugin — cap.Plugins.Directory
            // returns undefined. Pass the plain 'CACHE' string
            // the bridge accepts directly.
            const Filesystem = cap.Plugins?.Filesystem;
            const Share = cap.Plugins?.Share;
            if (Filesystem?.writeFile) {
                const resp = await fetch(url, { credentials: 'omit' });
                const blob = await resp.blob();
                const buf = await blob.arrayBuffer();
                let bin = '';
                const bytes = new Uint8Array(buf);
                for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
                const b64 = btoa(bin);
                const ext = (url.split('.').pop() || 'jpg').replace(/[^a-z0-9]/gi, '').slice(0, 4) || 'jpg';
                const name = `trailsiq-photo-${Date.now()}.${ext}`;
                await Filesystem.writeFile({
                    path: name,
                    data: b64,
                    directory: 'CACHE',
                });
                if (Share?.share) {
                    const uri = await Filesystem.getUri({ path: name, directory: 'CACHE' });
                    try {
                        await Share.share({ files: [uri.uri] });
                    } catch (shareErr) {
                        // User cancelled or share sheet dismissed —
                        // silent, matches iOS Photos behaviour.
                        if (!/cancel/i.test(String(shareErr?.message ?? ''))) throw shareErr;
                    }
                    return;
                }
                // No Share plugin in bundle (older shell) — fall
                // through to a Documents write so the user can
                // still get at the image via Files.app.
                await Filesystem.writeFile({
                    path: name,
                    data: b64,
                    directory: 'DOCUMENTS',
                });
                alert(`Saved to Files → On My iPhone → TrailsIQ as ${name}.`);
                return;
            }
        }
        // Web fallback — spawn an <a download> click. Same-
        // origin images download in place; cross-origin ones
        // open in a new tab (browser security), which the
        // user can then save with the OS's right-click.
        const a = document.createElement('a');
        a.href = url;
        a.download = url.split('/').pop() || 'photo.jpg';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { document.body.removeChild(a); } catch {} }, 100);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[lightbox] download failed', err);
    } finally {
        downloading.value = false;
    }
};

const onKey = (e) => {
    if (!isOpen.value) return;
    if (e.key === 'Escape') emit('close');
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
};

// ── Non-passive touch + iOS gesture wiring ──────────────────────
// Vue's @touchmove.prevent binding is passive in some browser
// paths (notably iOS WKWebView post-16.4), which means our
// preventDefault() is silently ignored and the WebView escalates
// the 2-finger touch into its own pinch-to-zoom gesture on the
// whole page. Attaching the listeners manually with { passive:
// false } guarantees preventDefault actually cancels default.
// The webkit-only gesturestart/change/end events fire alongside
// touch events on iOS Safari; preventing them is what actually
// stops the WKWebView's native zoom on some iOS versions where
// touch-action:none alone doesn't defeat it.
const _preventGesture = (e) => { e.preventDefault?.(); };
const _touchStartOpts = { passive: false };
const _touchMoveOpts = { passive: false };
// WebKit GestureEvents target the <img> but do NOT bubble, so
// element-level listeners (on the img or the overlay) never fire.
// A document-level CAPTURE listener is the only place they reliably
// land — verified on-device. Bound only while the lightbox is open.
const _captureOpts = { passive: false, capture: true };

let _boundImg = null;
let _boundOverlay = null;

function bindGestureListeners() {
    if (!imgEl.value || _boundImg === imgEl.value) return;
    // Clean up any prior binding — the img element can be
    // replaced between openings (Teleport unmount/remount) so
    // we always re-bind on the current node.
    unbindGestureListeners();
    _boundImg = imgEl.value;
    _boundImg.addEventListener('touchstart', onImgTouchStart, _touchStartOpts);
    _boundImg.addEventListener('touchmove', onImgTouchMove, _touchMoveOpts);
    _boundImg.addEventListener('touchend', onImgTouchEnd, _touchStartOpts);
    _boundImg.addEventListener('touchcancel', onImgTouchEnd, _touchStartOpts);
    _boundImg.addEventListener('wheel', onImgWheel, { passive: false });
    if (overlayEl.value && _boundOverlay !== overlayEl.value) {
        _boundOverlay = overlayEl.value;
        // Belt-and-suspenders on the backdrop: a two-finger touchmove
        // that straddles image / backdrop must not escalate to page zoom.
        _boundOverlay.addEventListener('touchmove', _preventGesture, _touchMoveOpts);
    }
}

function unbindGestureListeners() {
    if (_boundImg) {
        _boundImg.removeEventListener('touchstart', onImgTouchStart, _touchStartOpts);
        _boundImg.removeEventListener('touchmove', onImgTouchMove, _touchMoveOpts);
        _boundImg.removeEventListener('touchend', onImgTouchEnd, _touchStartOpts);
        _boundImg.removeEventListener('touchcancel', onImgTouchEnd, _touchStartOpts);
        _boundImg.removeEventListener('wheel', onImgWheel, { passive: false });
        _boundImg = null;
    }
    if (_boundOverlay) {
        _boundOverlay.removeEventListener('touchmove', _preventGesture, _touchMoveOpts);
        _boundOverlay = null;
    }
}

// Whenever the lightbox opens, wait for the img to mount then
// bind the listeners. Rebind when the src changes (the img node
// is the same but we make sure the binding is fresh in case a
// prior close/open cycle unmounted it).
watch(isOpen, async (open) => {
    if (typeof document !== 'undefined') {
        document.body.style.overflow = open ? 'hidden' : '';
    }
    if (open) {
        await nextTick();
        bindGestureListeners();
    } else {
        unbindGestureListeners();
    }
});

onMounted(() => {
    document.addEventListener('keydown', onKey);
    // iOS pinch-zoom: WebKit GestureEvents don't bubble and only land on
    // a document-level CAPTURE listener, so bind here unconditionally
    // (not tied to the img/overlay ref lifecycle). The handlers no-op
    // via their isOpen guard when the lightbox is closed.
    document.addEventListener('gesturestart', onGestureStart, _captureOpts);
    document.addEventListener('gesturechange', onGestureChange, _captureOpts);
    document.addEventListener('gestureend', onGestureEnd, _captureOpts);
});
onBeforeUnmount(() => {
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('gesturestart', onGestureStart, _captureOpts);
    document.removeEventListener('gesturechange', onGestureChange, _captureOpts);
    document.removeEventListener('gestureend', onGestureEnd, _captureOpts);
    document.body.style.overflow = '';
    unbindGestureListeners();
});
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition-opacity duration-200"
            enter-from-class="opacity-0"
            leave-active-class="transition-opacity duration-150"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isOpen"
                ref="overlayEl"
                class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
                style="touch-action: none;"
                @click.self="onBackdropClick"
            >
                <!-- Counter -->
                <div
                    v-if="images.length > 1"
                    class="absolute left-1/2 top-4 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white"
                >
                    {{ current + 1 }} / {{ images.length }}
                </div>

                <!-- Top action row: safe-area-aware, higher
                     contrast, bigger tap targets. White pill so
                     the buttons dont vanish against a light
                     background image + iOS notch clearance so
                     the corner isnt hidden by the status bar. -->
                <div
                    class="absolute right-4 flex items-center gap-2"
                    style="top:calc(var(--tiq-safe-top, env(safe-area-inset-top, 0px)) + 12px);"
                >
                    <button
                        type="button"
                        class="flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-900 shadow-lg ring-1 ring-black/10 hover:bg-gray-100 disabled:opacity-60"
                        aria-label="Save photo"
                        :disabled="downloading"
                        @click="doDownload"
                    >
                        <svg
                            v-if="!downloading"
                            class="h-6 w-6"
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
                        </svg>
                        <span
                            v-else
                            class="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-900 animate-spin"
                        ></span>
                    </button>
                    <button
                        type="button"
                        class="flex h-11 w-11 items-center justify-center rounded-full bg-white text-gray-900 shadow-lg ring-1 ring-black/10 hover:bg-gray-100"
                        aria-label="Close"
                        @click="emit('close')"
                    >
                        <svg class="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.4">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 6l12 12M6 18L18 6" />
                        </svg>
                    </button>
                </div>

                <!-- Prev -->
                <button
                    v-if="images.length > 1"
                    type="button"
                    class="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:bg-black/70"
                    aria-label="Previous photo"
                    @click="prev"
                >
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>

                <!-- Image — pinch to zoom, single-finger pan when
                     zoomed, double-tap to toggle 1× ↔ 2.5×.
                     Desktop parity: Ctrl/Cmd + wheel zooms towards
                     the cursor. Transform + touch handlers are
                     wired in <script>. -->
                <img
                    ref="imgEl"
                    :src="images[current]"
                    :alt="captions[current] || `Photo ${current + 1}`"
                    class="max-h-[90vh] max-w-[90vw] rounded object-contain shadow-2xl"
                    draggable="false"
                    :style="imgTransformStyle"
                    @dblclick="onImgDblClick"
                    @click.stop
                />

                <!-- The description, over the image rather than under
                     it: the image is centred in the viewport and sized
                     to fit, so there is no reliable "under" to put a
                     bar in — it would sit somewhere different for every
                     picture. Pointer-events stay off so it cannot eat a
                     pan gesture on a zoomed photo. -->
                <div
                    v-if="captions[current]"
                    class="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 to-transparent px-6 pb-6 pt-12"
                >
                    <p class="mx-auto max-w-3xl text-center text-sm leading-relaxed text-white/95">
                        {{ captions[current] }}
                    </p>
                </div>

                <!-- Next -->
                <button
                    v-if="images.length > 1"
                    type="button"
                    class="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white hover:bg-black/70"
                    aria-label="Next photo"
                    @click="next"
                >
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            </div>
        </Transition>
    </Teleport>
</template>
